<template>
    <div :class="classes">
        <slot></slot>
        <div v-if="busy" class="overlay-busy">
            <text-spinner :size="busySize"></text-spinner>
        </div>
    </div>
</template>

<style lang="scss">
$fontSizes: (
    "xx-large": 20px,
    "x-large": 18px,
    "large": 16px,
    "normal": 14px,
    "small": 12px,
    "x-small": 10px,
    "xx-small": 8px,
);

@mixin buttonStyle() {
    button,
    a {
        @content;
    }
}

.coolButton {
    @include buttonStyle() {
        border-radius: 5px;
        padding: 0.5em;
        border: none;
        cursor: pointer;
        margin: 0;
        font-weight: 700;
    }

    &.busy-button {
        position: relative;

        @include buttonStyle() {
            opacity: 0.75;
        }

        .overlay-busy {
            position: absolute;
            top: 0px;
            left: 0px;
            right: 0px;
            bottom: 0px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
        }
    }

    a {
        display: inline-block;
    }

    &.option-inline {
        display: inline-block;
    }

    &.option-fullwidth {
        @include buttonStyle() {
            width: 100%;
        }
    }

    &.option-round {
        @include buttonStyle() {
            border-radius: 200%;
        }
    }
    @each $name, $size in $fontSizes {
        &.size-#{$name} {
            @include buttonStyle() {
                font-size: $size;
            }

            &.busy-button {
                .overlay-busy {
                    font-size: $size;
                }
            }
        }
    }

    &.size-x-small {
        @include buttonStyle() {
            padding: 0.2em 0.5em;
        }
    }
    &.size-xx-small {
        @include buttonStyle() {
            padding: 0.2em 0.5em;
        }
    }

    &.style-bold {
        @include buttonStyle() {
            font-weight: bold;
        }
    }
    &.theme-black {
        @include buttonStyle() {
            background-color: black;
            color: white;
        }
    }

    &.theme-turqoise {
        @include buttonStyle() {
            background-color: #02b6b5;
            color: black;
        }
    }
    &.option-uppercase {
        @include buttonStyle() {
            text-transform: uppercase;
        }
    }
}
</style>

<script setup lang="ts">
import { type PropType, computed } from "vue";
import textSpinner from "./TextSpinner.vue";

type TextStyleString = "bold" | "";

const props = defineProps({
    busySize: Number,
    theme: {
        type: String as PropType<"black" | "turqoise" | "">,
        default: "black",
    },
    size: {
        type: String as PropType<
            | "xx-large"
            | "x-large"
            | "large"
            | "normal"
            | "small"
            | "x-small"
            | "xx-small"
        >,
        default: "normal",
    },
    textStyle: {
        type: [String, Array] as PropType<TextStyleString | TextStyleString[]>,
        default: "bold",
    },
    inline: {
        type: Boolean,
        default: false,
    },
    fullWidth: {
        type: Boolean,
        default: false,
    },
    round: {
        type: Boolean,
        default: false,
    },
    busy: {
        type: Boolean,
        default: false,
    },
    upperCase: {
        type: Boolean,
        default: false,
    },
});
const styleName = computed(() => {
    if (typeof props.textStyle === "string") return props.textStyle;
    if (Array.isArray(props.textStyle)) return props.textStyle.concat().sort();
    return "none";
});
const classes = computed(() => {
    return [
        "coolButton",
        "theme-" + props.theme,
        "size-" + props.size,
        "style-" + styleName.value,
        props.fullWidth === true ? "option-fullwidth" : "",
        props.inline === true ? "option-inline" : "",
        props.round === true ? "option-round" : "",
        props.busy === true ? "busy-button" : "",
        props.upperCase === true ? "option-uppercase" : "",
    ];
});
</script>

<script lang="ts">
export default {
    name: "cool-button",
};
</script>
